/**
 * Identity → Compliance: zapnuté frameworky po tenantech (organizacích), kdo
 * co nesplňuje, co vyprší do 30 dní, pokrytí kontrol norem (ISO 27001, NIS2,
 * český zákon) s odkazy, požadavky s revizemi a pole s tím, kdo je smí potvrdit.
 * Data: identity.compliance_* (model viz docs/wiki-drafts/compliance.md).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ClipboardCheck, ExternalLink, ShieldCheck } from 'lucide-react';
import { mcpCall } from '../lib/mcp';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Select, Toggle } from '../components/ui';

interface TenantRow {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  member_count: number;
}
interface Framework {
  acm_uuid: string;
  key: string;
  label: string;
  description: string;
  norms: string[];
  requirement_keys: string[];
  activations: Array<{ tenant_id: string; enabled: boolean; at: string; by: string | null; reason?: string }>;
}
interface Overview {
  ok?: boolean;
  error?: string;
  users: number;
  tenants: string[];
  failing: Array<{ requirement: string; label: string; effect: string; users: number }>;
  expiring_30d: Array<{ entity_id: string; label: string; field: string; expires_at: string }>;
  users_failing: Array<{ entity_id: string; label: string; failing: number; blocked: number }>;
}
interface Jurisdiction {
  basis: 'tenant' | 'server';
  country: string | null;
  source: string;
  tenant_country: string | null;
  node_country: string | null;
}
interface Settings {
  ok?: boolean;
  settings: { jurisdiction_basis: 'tenant' | 'server'; country_override: string | null; version: number } | null;
  jurisdiction: Jurisdiction;
}
interface Coverage {
  ok?: boolean;
  error?: string;
  jurisdiction?: Jurisdiction;
  other_jurisdictions?: Array<{ norm: string; id: string; country: string | null }>;
  summary: Record<string, { total: number; enforced: number; monitored: number; manual: number; none: number }>;
  controls: Array<{
    control: { norm: string; id: string; title: string; url: string };
    status: 'enforced' | 'monitored' | 'manual' | 'none';
    requirements: string[];
    fields: string[];
  }>;
}
interface Requirement {
  acm_uuid: string;
  key: string;
  label: string;
  description: string;
  effect: string;
  scope: { type: string; id?: string };
  version: number;
  updated_by?: string | null;
  updated_at?: string;
  reason?: string;
  status: string;
  controls: Array<{ norm: string; id: string }>;
}
interface Field {
  acm_uuid: string;
  key: string;
  label: string;
  type: string;
  validity_days: number | null;
  evidence_required: boolean;
  confirmers: string[];
  controls: Array<{ norm: string; id: string }>;
  scope: { type: string; id?: string };
}

const NORM_LABEL: Record<string, string> = { 'iso27001:2022': 'ISO/IEC 27001:2022', nis2: 'NIS2', 'cz-zkb': 'ZKB (CZ)' };
const STATUS_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'secondary'> = {
  enforced: 'success',
  monitored: 'info',
  manual: 'warning',
  none: 'secondary',
};
const fmt = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('cs-CZ') : '—');

export function IdentityComplianceTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tenant, setTenant] = useState<string>('');
  const [norm, setNorm] = useState<string>('');

  const tenants = useQuery<{ ok?: boolean; summaries?: TenantRow[] }>({
    queryKey: ['identity-tenant-summary'],
    queryFn: () => mcpCall('identity.tenant_summary'),
  });
  const rows = tenants.data?.summaries ?? [];
  const current = tenant || rows[0]?.tenant_id || '';

  const frameworks = useQuery<{ ok?: boolean; frameworks?: Framework[] }>({
    queryKey: ['compliance-frameworks'],
    queryFn: () => mcpCall('identity.compliance_frameworks'),
  });
  const overview = useQuery<Overview>({
    queryKey: ['compliance-overview', current],
    enabled: !!current,
    queryFn: () => mcpCall('identity.compliance_overview', { tenant: current }),
  });
  const coverage = useQuery<Coverage>({
    queryKey: ['compliance-coverage', current, norm],
    enabled: !!current,
    queryFn: () => mcpCall('identity.compliance_coverage', { tenant: current, ...(norm ? { norm } : {}) }),
  });
  const requirements = useQuery<{ ok?: boolean; requirements?: Requirement[] }>({
    queryKey: ['compliance-requirements'],
    queryFn: () => mcpCall('identity.compliance_requirements'),
  });
  const fields = useQuery<{ ok?: boolean; fields?: Field[] }>({
    queryKey: ['compliance-fields', current],
    enabled: !!current,
    queryFn: () => mcpCall('identity.compliance_fields', { tenant: current }),
  });
  const settings = useQuery<Settings>({
    queryKey: ['compliance-settings', current],
    enabled: !!current,
    queryFn: () => mcpCall('identity.compliance_settings', { tenant: current }),
  });
  const [overrideInput, setOverrideInput] = useState('');
  const setSettings = useMutation({
    mutationFn: async (a: Record<string, unknown>) => {
      const r = await mcpCall<{ ok?: boolean; error?: string }>('identity.compliance_settings_set', { tenant: current, ...a, reason: t('complianceUi.toggleReason') });
      if (r?.ok === false) throw new Error(r.error ?? t('complianceUi.error'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-settings'] });
      qc.invalidateQueries({ queryKey: ['compliance-coverage'] });
      qc.invalidateQueries({ queryKey: ['compliance-overview'] });
      toast.success(t('complianceUi.saved'));
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });

  const setFramework = useMutation({
    mutationFn: async (a: { framework: string; enabled: boolean }) => {
      const r = await mcpCall<{ ok?: boolean; error?: string }>('identity.compliance_framework_set', {
        framework: a.framework,
        tenant: current,
        enabled: a.enabled,
        reason: t('complianceUi.toggleReason'),
      });
      if (r?.ok === false) throw new Error(r.error ?? t('complianceUi.error'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-frameworks'] });
      qc.invalidateQueries({ queryKey: ['compliance-overview'] });
      qc.invalidateQueries({ queryKey: ['compliance-coverage'] });
      toast.success(t('complianceUi.saved'));
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const reconcile = useMutation({
    mutationFn: () => mcpCall('identity.compliance_reconcile', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-overview'] });
      toast.success(t('complianceUi.reconciled'));
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });

  if (tenants.isLoading) return <p className='text-sm text-gray-500'>{t('complianceUi.loading')}</p>;
  if (!rows.length) return <EmptyState icon={<ClipboardCheck size={28} />} title={t('complianceUi.noTenants')} />;

  const enabledOn = (f: Framework) => f.activations.some((a) => a.enabled && overview.data?.tenants?.includes(a.tenant_id));
  const ov = overview.data;
  const cov = coverage.data;

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-3'>
        <ClipboardCheck size={16} className='text-gray-400' />
        <span className='text-sm text-gray-300'>{t('complianceUi.tenant')}</span>
        <Select
          size='sm'
          value={current}
          onChange={(e) => setTenant(e.target.value)}
          options={rows.map((r) => ({ value: r.tenant_id, label: `${r.tenant_name} (${r.tenant_slug})` }))}
        />
        <div className='flex-1' />
        <Button size='sm' variant='secondary' disabled={reconcile.isPending} onClick={() => reconcile.mutate()}>
          {t('complianceUi.reconcile')}
        </Button>
      </div>

      <div className='grid gap-4 lg:grid-cols-2'>
        <Card>
          <CardHeader>
            <CardTitle>{t('complianceUi.frameworksTitle')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-2'>
            <p className='text-[11px] text-gray-500'>{t('complianceUi.frameworksHint')}</p>
            {(frameworks.data?.frameworks ?? []).map((f) => (
              <div key={f.acm_uuid} className='flex items-start gap-3 rounded-lg border border-gray-800 p-2'>
                <Toggle value={enabledOn(f)} saving={setFramework.isPending} onSave={(v) => setFramework.mutateAsync({ framework: f.key, enabled: v })} />
                <div className='min-w-0 flex-1'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span className='text-sm font-medium text-white'>{f.label}</span>
                    {f.norms.map((n) => <Badge key={n} variant='info'>{NORM_LABEL[n] ?? n}</Badge>)}
                  </div>
                  <p className='text-[11px] text-gray-500'>{f.description}</p>
                  <p className='font-mono text-[10px] text-gray-600'>{f.requirement_keys.join(', ')}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('complianceUi.overviewTitle')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 text-sm'>
            {ov?.ok === false && <p className='text-amber-400'>{ov.error}</p>}
            {ov && ov.ok !== false && (
              <>
                <div className='flex flex-wrap gap-2 text-xs'>
                  <Badge variant='secondary'>{t('complianceUi.users')}: {ov.users}</Badge>
                  <Badge variant={ov.users_failing.length ? 'warning' : 'success'}>{t('complianceUi.usersFailing')}: {ov.users_failing.length}</Badge>
                  <Badge variant={ov.expiring_30d.length ? 'warning' : 'secondary'}>{t('complianceUi.expiring')}: {ov.expiring_30d.length}</Badge>
                </div>
                {ov.failing.length === 0 && <p className='text-xs text-gray-500'>{t('complianceUi.allGood')}</p>}
                {ov.failing.map((f) => (
                  <div key={f.requirement} className='flex items-center gap-2 text-xs'>
                    <Badge variant={f.effect === 'block' ? 'error' : f.effect === 'warn' ? 'warning' : 'secondary'}>{f.effect}</Badge>
                    <span className='text-gray-200'>{f.label}</span>
                    <span className='text-gray-500'>{f.users}×</span>
                  </div>
                ))}
                {ov.users_failing.length > 0 && (
                  <div className='pt-1 text-xs text-gray-400'>
                    {ov.users_failing.slice(0, 12).map((u) => (
                      <a key={u.entity_id} href={`?entity=${u.entity_id}`} className='mr-2 text-plex hover:underline'>
                        {u.label} ({u.failing}{u.blocked ? ` · ${t('complianceUi.blocked')} ${u.blocked}` : ''})
                      </a>
                    ))}
                  </div>
                )}
                {ov.expiring_30d.length > 0 && (
                  <div className='pt-1 text-xs text-gray-400'>
                    {t('complianceUi.expiringList')}: {ov.expiring_30d.slice(0, 12).map((x) => `${x.label} · ${x.field} · ${fmt(x.expires_at)}`).join('; ')}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('complianceUi.jurisdictionTitle')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-2 text-sm'>
          <p className='text-[11px] text-gray-500'>{t('complianceUi.jurisdictionHint')}</p>
          <div className='flex flex-wrap items-center gap-3'>
            <span className='text-xs text-gray-400'>{t('complianceUi.jurisdictionBasis')}</span>
            <Select
              size='sm'
              value={settings.data?.settings?.jurisdiction_basis ?? 'tenant'}
              onChange={(e) => setSettings.mutate({ jurisdiction_basis: e.target.value })}
              options={[
                { value: 'tenant', label: t('complianceUi.basisTenant') },
                { value: 'server', label: t('complianceUi.basisServer') },
              ]}
            />
            <Badge variant={settings.data?.jurisdiction?.country ? 'success' : 'warning'}>
              {t('complianceUi.effectiveCountry')}: {settings.data?.jurisdiction?.country ?? t('complianceUi.unknownCountry')}
              {settings.data?.jurisdiction?.source ? ` · ${t(`complianceUi.source_${settings.data.jurisdiction.source}`)}` : ''}
            </Badge>
            <span className='text-xs text-gray-500'>
              {t('complianceUi.tenantCountry')}: {settings.data?.jurisdiction?.tenant_country ?? '—'} · {t('complianceUi.nodeCountry')}: {settings.data?.jurisdiction?.node_country ?? '—'}
            </span>
          </div>
          <form
            className='flex flex-wrap items-center gap-2'
            onSubmit={(e) => {
              e.preventDefault();
              setSettings.mutate({ country_override: overrideInput.trim().toUpperCase() || null });
              setOverrideInput('');
            }}
          >
            <span className='text-xs text-gray-400'>{t('complianceUi.override')}</span>
            <input
              value={overrideInput}
              onChange={(e) => setOverrideInput(e.target.value)}
              placeholder={settings.data?.settings?.country_override ?? 'CZ'}
              maxLength={2}
              className='w-16 rounded border border-gray-700 bg-gray-900 px-2 py-1 font-mono text-xs uppercase text-gray-200'
            />
            <Button size='sm' type='submit' disabled={setSettings.isPending}>{t('complianceUi.overrideSet')}</Button>
            {settings.data?.settings?.country_override && (
              <Button size='sm' variant='secondary' disabled={setSettings.isPending} onClick={() => setSettings.mutate({ country_override: null })}>
                {t('complianceUi.overrideClear')}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <span className='flex items-center gap-2'>
              <ShieldCheck size={14} /> {t('complianceUi.coverageTitle')}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <Select
              size='sm'
              value={norm}
              onChange={(e) => setNorm(e.target.value)}
              options={[{ value: '', label: t('complianceUi.allNorms') }, ...Object.keys(NORM_LABEL).map((n) => ({ value: n, label: NORM_LABEL[n]! }))]}
            />
            {cov?.summary && Object.entries(cov.summary).map(([n, s]) => (
              <span key={n} className='rounded-md border border-gray-800 px-2 py-1 text-xs text-gray-300'>
                {NORM_LABEL[n] ?? n}: {s.enforced}/{s.monitored}/{s.manual}/{s.none}
              </span>
            ))}
            <span className='text-[11px] text-gray-500'>{t('complianceUi.coverageLegend')}</span>
            {cov?.other_jurisdictions?.length ? (
              <span className='text-[11px] text-gray-600'>
                {t('complianceUi.otherJurisdictions')}: {cov.other_jurisdictions.map((o) => `${o.norm} (${o.country ?? '?'})`).join(', ')}
              </span>
            ) : null}
          </div>
          <div className='divide-y divide-gray-800/60'>
            {(cov?.controls ?? []).map((c) => (
              <div key={`${c.control.norm}:${c.control.id}`} className='flex flex-wrap items-center gap-2 py-1 text-xs'>
                <Badge variant={STATUS_VARIANT[c.status]}>{t(`complianceUi.status_${c.status}`)}</Badge>
                <span className='font-mono text-gray-300'>{c.control.id}</span>
                <span className='min-w-0 flex-1 truncate text-gray-400'>{c.control.title}</span>
                {c.requirements.length > 0 && <span className='font-mono text-[10px] text-gray-500'>{c.requirements.join(', ')}</span>}
                <a href={c.control.url} target='_blank' rel='noreferrer' className='text-plex hover:underline'>
                  {NORM_LABEL[c.control.norm] ?? c.control.norm} <ExternalLink size={10} className='inline' />
                </a>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className='grid gap-4 lg:grid-cols-2'>
        <Card>
          <CardHeader>
            <CardTitle>{t('complianceUi.requirementsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className='divide-y divide-gray-800/60'>
            {(requirements.data?.requirements ?? []).map((r) => (
              <div key={r.acm_uuid} className='py-1.5 text-xs'>
                <div className='flex flex-wrap items-center gap-2'>
                  <Badge variant={r.effect === 'block' ? 'error' : r.effect === 'warn' ? 'warning' : 'secondary'}>{r.effect}</Badge>
                  <span className='font-medium text-gray-200'>{r.label}</span>
                  <span className='font-mono text-[10px] text-gray-500'>{r.key} · v{r.version}</span>
                  <Badge variant='secondary'>{r.scope.type}{r.scope.id ? `:${r.scope.id}` : ''}</Badge>
                  {r.controls.map((c) => <span key={`${c.norm}${c.id}`} className='font-mono text-[10px] text-gray-500'>{c.norm} {c.id}</span>)}
                </div>
                <p className='text-gray-500'>{r.description}</p>
                {(r.updated_by || r.reason) && (
                  <p className='text-[10px] text-gray-600'>
                    {t('complianceUi.revision')}: {r.updated_by ?? '—'} · {fmt(r.updated_at)}{r.reason ? ` · ${r.reason}` : ''}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('complianceUi.fieldsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className='divide-y divide-gray-800/60'>
            {(fields.data?.fields ?? []).map((f) => (
              <div key={f.acm_uuid} className='py-1.5 text-xs'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='font-medium text-gray-200'>{f.label}</span>
                  <span className='font-mono text-[10px] text-gray-500'>{f.key} · {f.type}</span>
                  {f.validity_days && <Badge variant='secondary'>{f.validity_days} {t('complianceUi.days')}</Badge>}
                  {f.evidence_required && <Badge variant='warning'>{t('complianceUi.evidence')}</Badge>}
                  {f.scope.type === 'tenant' && <Badge variant='info'>{t('complianceUi.tenantField')}</Badge>}
                </div>
                <p className='text-gray-500'>
                  {t('complianceUi.confirmers')}: {f.confirmers.map((c) => t(`complianceUi.confirmer_${c}`)).join(', ')}
                  {f.controls.length ? ` · ${f.controls.map((c) => `${c.norm} ${c.id}`).join(', ')}` : ''}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
